import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Live Production Shows",
  "Character Creation & Brand Development",
  "Cabaret Acts & Tribute Entertainment",
  "Screen Content & Promotional Media",
  "Entertainment Team Training",
  "Sound, Lighting & LED Screen Installations",
  "Complete Entertainment Package",
  "Other",
];

const inputClass =
  "w-full bg-[#121212] border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#FF2A2A]/60 transition-colors duration-200";

const initialForm = { name: "", company: "", venue: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in your name, email, service required and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent!", { description: "Thank you — we'll be in touch shortly." });
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please check your details and try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-14 lg:py-20 overflow-hidden">
      <div className="absolute -bottom-40 right-1/4 w-[600px] h-[600px] rounded-full bg-[#FF2A2A]/8 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-sm sm:text-base font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-4">Contact</p>
          <h2 className="font-heading heading-fluid font-medium tracking-normal text-metal">
            Ready To Elevate Your Entertainment?
          </h2>
          <p className="mt-8 text-lg text-zinc-300 leading-relaxed">
            Whether you need a complete entertainment partner, a new production show, professional cabaret acts, team
            training, character development, visual content or a technical installation, Robust Entertainment can help.
          </p>
          <p className="mt-4 text-lg text-zinc-300 leading-relaxed">
            Tell us what you are looking to achieve and we will help you build the right solution.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <div className="grid gap-4 text-left" data-testid="contact-details">
            <a
              href="tel:+447539528505"
              className="flex items-start gap-4 bg-[#121212] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl p-5 transition-colors duration-200"
              data-testid="contact-phone"
            >
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 shrink-0">
                <Phone size={18} className="text-[#D4AF37]" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold mb-1">Telephone</p>
                <p className="text-sm text-zinc-200 leading-snug">07539 528 505</p>
              </div>
            </a>
            <a
              href="mailto:paul@robustentertainment.com"
              className="flex items-start gap-4 bg-[#121212] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl p-5 transition-colors duration-200"
              data-testid="contact-email"
            >
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 shrink-0">
                <Mail size={18} className="text-[#D4AF37]" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold mb-1">Email</p>
                <p className="text-sm text-zinc-200 leading-snug truncate">paul@robustentertainment.com</p>
              </div>
            </a>
            <a
              href="https://www.robustentertainment.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 bg-[#121212] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl p-5 transition-colors duration-200"
              data-testid="contact-website"
            >
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 shrink-0">
                <Globe size={18} className="text-[#D4AF37]" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold mb-1">Website</p>
                <p className="text-sm text-zinc-200 leading-snug truncate">www.robustentertainment.com</p>
              </div>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-name-input" className={inputClass} placeholder="Name *" value={form.name} onChange={set("name")} />
            <input data-testid="contact-company-input" className={inputClass} placeholder="Company" value={form.company} onChange={set("company")} />
          </div>
          <input data-testid="contact-venue-input" className={inputClass} placeholder="Venue / Organisation" value={form.venue} onChange={set("venue")} />
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-email-input" type="email" className={inputClass} placeholder="Email *" value={form.email} onChange={set("email")} />
            <input data-testid="contact-phone-input" className={inputClass} placeholder="Phone" value={form.phone} onChange={set("phone")} />
          </div>
          <select
            data-testid="contact-service-select"
            className={`${inputClass} ${form.service ? "text-white" : "text-zinc-400"}`}
            value={form.service}
            onChange={set("service")}
          >
            <option value="" disabled>
              Service Required *
            </option>
            {services.map((s) => (
              <option key={s} value={s} className="text-white bg-[#121212]">
                {s}
              </option>
            ))}
          </select>
          <textarea
            data-testid="contact-message-input"
            className={`${inputClass} min-h-[140px] resize-y`}
            placeholder="Message *"
            value={form.message}
            onChange={set("message")}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="btn-glow-red w-full bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white font-medium px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
          >
            {sending && <Loader2 size={18} className="animate-spin" />}
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
